/*
 * Copyright 2021 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import PropTypes from 'prop-types';
import StoryPropTypes from '../../../src/types';
import getGoogleFontURL from '../../utils/getGoogleFontURL';
import getFontCSS from '../../utils/getFontCSS';
import getTextFontDefinitions from './getTextFontDefinitions';
import NotoEmojiFontDeclaration from './NotoEmojiFontDeclaration';

const fontMarker = { 'data-font-def': true };

function FontDeclarations({ pages }) {
  const definitions = getTextFontDefinitions(pages);

  return (
    <>
      {Array.from(definitions.keys()).map((service) => {
        const fonts = definitions.get(service);
        switch (service) {
          case 'fonts.google.com':
            return (
              <link
                key={service}
                href={getGoogleFontURL(fonts)}
                rel="stylesheet"
                {...fontMarker}
              />
            );
          case 'custom':
            return fonts.map(({ key, family, url, urls, metricOverrides }) => {
              const inlineStyle = getFontCSS(
                family,
                url,
                urls,
                metricOverrides
              );
              return (
                <style
                  key={key}
                  dangerouslySetInnerHTML={{
                    __html: inlineStyle,
                  }}
                  {...fontMarker}
                />
              );
            });
          default:
            return null;
        }
      })}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
      <NotoEmojiFontDeclaration />
    </>
  );
}

FontDeclarations.propTypes = {
  pages: PropTypes.arrayOf(StoryPropTypes.page).isRequired,
};

export default FontDeclarations;
